import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert, Card, Badge } from 'react-bootstrap'
import { formatErrorResponse } from '../utils/errorHandlers'
import { userAPI } from '../api/userAPI'
import TwoFactorManagement from '../components/TwoFactorManagement'

function UserProfileScreen() {
    const navigate = useNavigate()
    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(true)
    const [user, setUser] = useState({
        username: '',
        first_name: '',
        last_name: '',
        email: '',
        is_active: true
    })

    useEffect(() => {
        async function fetchCurrentUser() {
            try {
                setLoading(true)
                const data = await userAPI.getCurrentUser()
                setUser(data)
            } catch (error) {
                console.error('Error fetching current user:', error)
                setError('Failed to load user profile data')
            } finally {
                setLoading(false)
            }
        }
        fetchCurrentUser()
    }, [])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            await userAPI.update(user.id, user)
            // Update localStorage with new user data
            if (user.first_name) {
                localStorage.setItem('user_first_name', user.first_name)
            }
            setError(null)
            // Navigate back to previous screen
            navigate(-1)
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    if (loading) {
        return (
            <Container>
                <Row className="justify-content-left">
                    <Col md={8} lg={6}>
                        <h1>My Profile</h1>
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Loading profile...</p>
                        </div>
                    </Col>
                </Row>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <h1 style={{ opacity: user.is_active ? 1 : 0.6 }} className="mb-0">
                            My Profile
                        </h1>
                        {user.is_superuser && (
                            <Badge bg="success" className="fs-6">
                                Superuser
                            </Badge>
                        )}
                        {user.is_permission_admin && (
                            <Badge bg="danger" className="fs-6">
                                Permission Admin
                            </Badge>
                        )}
                    </div>
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="username">
                            <Form.Label>Username*</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter username"
                                value={user.username}
                                onChange={(e) => setUser({ ...user, username: e.target.value })}
                                disabled={!user.is_active}
                                style={{ opacity: user.is_active ? 1 : 0.6 }}
                                required
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="first_name">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter first name"
                                value={user.first_name}
                                onChange={(e) => setUser({ ...user, first_name: e.target.value })}
                                disabled={!user.is_active}
                                style={{ opacity: user.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="last_name">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter last name"
                                value={user.last_name}
                                onChange={(e) => setUser({ ...user, last_name: e.target.value })}
                                disabled={!user.is_active}
                                style={{ opacity: user.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="email">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter email"
                                value={user.email}
                                onChange={(e) => setUser({ ...user, email: e.target.value })}
                                disabled={!user.is_active}
                                style={{ opacity: user.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        {/* Two-Factor Authentication Section */}
                        <div className="mb-4">
                            <TwoFactorManagement />
                        </div>

                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}
                        <Row className="mt-3">
                            <Col>
                                {user.is_active ? (
                                    <>
                                        <Button 
                                            variant="primary" 
                                            type="submit" 
                                            className="me-2"
                                        >
                                            Update
                                        </Button>
                                        <Button 
                                            variant="warning"
                                            onClick={() => navigate(`/user/${user.id}/change-password`)}
                                            className="me-2"
                                        >
                                            Change Password
                                        </Button>
                                        <Button 
                                            variant="secondary" 
                                            onClick={() => navigate('/dashboard')}
                                        >
                                            Cancel
                                        </Button>
                                    </>
                                ) : (
                                    <Alert variant="warning" className="mt-3">
                                        This user is currently inactive. Please contact an administrator.
                                    </Alert>
                                )}
                            </Col>
                        </Row>

                        {!user.is_active && (
                            <Alert variant="warning" className="mt-3">
                                This user is currently inactive. Activate it to make changes.
                            </Alert>
                        )}
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default UserProfileScreen
